/**
 * ============================================================================
 * SupplierDetail — Phase 6d frontend (Supplier Management Module).
 * Owner: Claude 3 (Frontend & UX).
 * Route: /suppliers/:id
 * Purpose: full supplier dossier — header, seven tabs (timeline / KPIs /
 *          risk assessments / audits / CAPAs / vendor evaluations /
 *          documents), and an actions sidebar that opens transition and
 *          disqualification dialogs.
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Editorial layout, generous whitespace.
 *   - Tabs are real <button role="tab"> with arrow-key navigation.
 *   - The risk-score gauge in the header is inline SVG with a gradient stroke.
 * ============================================================================ */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, AreaTrend } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './SupplierDetail.module.css';
import { useSupplierDetailQuery } from './useSupplierDetailQuery';
import { SupplierTimeline } from './SupplierTimeline';
import { KpiTilesGrid } from './KpiTilesGrid';
import { RiskAssessmentsTable } from './RiskAssessmentsTable';
import { AuditsTable } from './AuditsTable';
import { CapasTable } from './CapasTable';
import { TransitionDialog } from './TransitionDialog';
import { DisqualifyDialog } from './DisqualifyDialog';
import { AddAgreementDialog, ScheduleAuditDialog, RecordAuditOutcomeDialog, RiskAssessmentDialog, RequalificationDialog, QuestionnaireDialog } from './SupplierQualificationDialogs';
import type {
  RiskCategory,
  SupplierCriticality,
  SupplierLifecycleStatus,
} from '../SupplierWorkbench/useSuppliersQuery';

type Tab =
  | 'qualification'
  | 'timeline'
  | 'kpis'
  | 'risk'
  | 'audits'
  | 'capas'
  | 'evaluations'
  | 'documents';

const TABS: Tab[] = [
  'qualification',
  'timeline',
  'kpis',
  'risk',
  'audits',
  'capas',
  'evaluations',
  'documents',
];

type DialogState =
  | { kind: 'closed' }
  | { kind: 'transition' }
  | { kind: 'disqualify' };

export interface SupplierDetailProps {
  supplierId?: string;
}

export function SupplierDetail({ supplierId = 'sup-demo' }: SupplierDetailProps = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const detailQ = useSupplierDetailQuery(supplierId);
  const data = detailQ.data;

  const [tab, setTab] = useState<Tab>('qualification');
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
  const [addAgreementOpen, setAddAgreementOpen] = useState(false);
  const [scheduleAuditOpen, setScheduleAuditOpen] = useState(false);
  const [recordOutcomeOpen, setRecordOutcomeOpen] = useState(false);
  const [riskOpen, setRiskOpen] = useState(false);
  const [requalOpen, setRequalOpen] = useState(false);
  const [qnOpen, setQnOpen] = useState(false);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  function onTabKey(e: React.KeyboardEvent<HTMLButtonElement>, idx: number) {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (idx + 1) % TABS.length;
      tabRefs.current[next]?.focus();
      setTab(TABS[next]!);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (idx - 1 + TABS.length) % TABS.length;
      tabRefs.current[prev]?.focus();
      setTab(TABS[prev]!);
    } else if (e.key === 'Home') {
      e.preventDefault();
      tabRefs.current[0]?.focus();
      setTab(TABS[0]!);
    } else if (e.key === 'End') {
      e.preventDefault();
      const last = TABS.length - 1;
      tabRefs.current[last]?.focus();
      setTab(TABS[last]!);
    }
  }

  useEffect(() => {
    /* no-op: kept for parity with the other tabbed screens */
  }, [tab]);

  return (
    <div className={wstyles.adminScreen}>
      {detailQ.isLoading && (
        <Card>
          <CardBody>
            <div role="status" aria-live="polite">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className={styles.skeletonRow} aria-hidden="true" />
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {detailQ.isError && (
        <Card>
          <CardBody>
            <div role="alert" className={styles.errorBanner}>
              <span>
                {t('supplier.detail.errors.loadFailed', {
                  defaultValue: 'Could not load supplier dossier.',
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => detailQ.refetch()}
              >
                {t('common.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {!detailQ.isLoading && !detailQ.isError && data && (
        <>
          <SupplierHeader
            name={data.name}
            criticality={data.criticality}
            subtype={data.subtype}
            lifecycleStatus={data.lifecycle_status}
            riskScore={data.risk_score}
            riskCategory={data.risk_category}
            nextRequalAtUtc={data.next_requalification_at_utc}
            dateFmt={dateFmt}
          />

          {/* SUP-FULL — always-visible identity & contact (the full supplier
              profile captured at add-time; renders even before any
              qualification sub-records exist). */}
          {(data.legal_name || data.country || data.tax_id || data.contact_email || data.single_source) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.5rem', padding: '0.5rem 0.75rem', fontSize: 14, color: 'var(--color-text-muted, #6B7989)' }}>
              {data.legal_name && <span>Legal entity: <strong>{data.legal_name}</strong></span>}
              {data.country && <span>Country: <strong>{data.country}</strong></span>}
              {data.tax_id && <span>VAT / Tax id: <strong>{data.tax_id}</strong></span>}
              {data.contact_email && <span>Contact: <strong>{data.contact_email}</strong>{data.contact_phone ? ` · ${data.contact_phone}` : ''}</span>}
              {data.single_source && <span style={{ color: 'var(--color-status-warning, #D97706)' }}><strong>Single-source</strong></span>}
            </div>
          )}

          <div className={styles.layout}>
            <main className={styles.mainCol}>
              <Card>
                <CardHeader>
                  <div
                    role="tablist"
                    aria-label={t('supplier.detail.tabs.listAria', {
                      defaultValue: 'Supplier dossier sections',
                    })}
                    className={styles.tablist}
                  >
                    {TABS.map((tk, idx) => {
                      const active = tk === tab;
                      return (
                        <button
                          key={tk}
                          ref={(el) => {
                            tabRefs.current[idx] = el;
                          }}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          aria-controls={`sup-tabpanel-${tk}`}
                          id={`sup-tab-${tk}`}
                          tabIndex={active ? 0 : -1}
                          className={`${styles.tabButton} ${active ? styles.tabButtonActive : ''}`}
                          onClick={() => setTab(tk)}
                          onKeyDown={(e) => onTabKey(e, idx)}
                        >
                          {t(`supplier.detail.tabs.${tk}`, {
                            defaultValue: tk,
                          })}
                        </button>
                      );
                    })}
                  </div>
                </CardHeader>
                <CardBody>
                  <div
                    role="tabpanel"
                    id={`sup-tabpanel-${tab}`}
                    aria-labelledby={`sup-tab-${tab}`}
                  >
                    {tab === 'qualification' && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
                          <Button type="button" variant="secondary" size="sm" leadingIcon="+" onClick={() => setRequalOpen(true)}>
                            {t('supplier.detail.requal.addBtn', { defaultValue: 'Record requalification' })}
                          </Button>
                          <Button type="button" variant="secondary" size="sm" leadingIcon="+" onClick={() => setQnOpen(true)}>
                            {t('supplier.detail.questionnaire.addBtn', { defaultValue: 'Record questionnaire' })}
                          </Button>
                        </div>
                        <QualificationDossier data={data} />
                      </>
                    )}
                    {tab === 'timeline' && (
                      <SupplierTimeline events={data.timeline} />
                    )}
                    {tab === 'kpis' && (
                      <KpiTilesGrid
                        kpis={data.kpis}
                        aggregateQualityScore={data.aggregate_quality_score}
                      />
                    )}
                    {tab === 'risk' && (() => {
                      const periodFmt = new Intl.DateTimeFormat(i18n.language, { month: 'short', year: '2-digit' });
                      const riskTrend = [...data.risk_assessments]
                        .sort((a, b) => new Date(a.ts_utc).getTime() - new Date(b.ts_utc).getTime())
                        .map((r) => ({ period: periodFmt.format(new Date(r.ts_utc)), score: r.computed_score }));
                      return (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                            <Button type="button" variant="secondary" size="sm" leadingIcon="+" onClick={() => setRiskOpen(true)}>
                              {t('supplier.detail.risk.addBtn', { defaultValue: 'Record risk assessment' })}
                            </Button>
                          </div>
                          {riskTrend.length >= 2 && (
                            <div className={styles.riskTrend}>
                              <h3 className={styles.riskTrendLabel}>
                                {t('supplier.detail.risk.trendTitle', { defaultValue: 'Risk score over time' })}
                              </h3>
                              <AreaTrend
                                data={riskTrend}
                                xKey="period"
                                series={[{ key: 'score', name: t('supplier.detail.risk.columns.score', { defaultValue: 'Score' }), color: 'var(--spectro-pink, #EC1D8C)' }]}
                                ariaLabel={t('supplier.detail.risk.trendTitle', { defaultValue: 'Risk score over time' })}
                                height={200}
                                valueFormatter={(v) => (typeof v === 'number' ? String(v) : String(v ?? ''))}
                              />
                            </div>
                          )}
                          <RiskAssessmentsTable rows={data.risk_assessments} />
                        </>
                      );
                    })()}
                    {tab === 'audits' && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
                          <Button type="button" variant="secondary" size="sm" leadingIcon="+" onClick={() => setScheduleAuditOpen(true)}>
                            {t('supplier.detail.audit.scheduleBtn', { defaultValue: 'Schedule audit' })}
                          </Button>
                          <Button type="button" variant="secondary" size="sm" onClick={() => setRecordOutcomeOpen(true)}>
                            {t('supplier.detail.auditOutcome.recordBtn', { defaultValue: 'Record outcome' })}
                          </Button>
                        </div>
                        <AuditsTable rows={data.audits} />
                      </>
                    )}
                    {tab === 'capas' && <CapasTable rows={data.capas} />}
                    {tab === 'evaluations' && (
                      <EvaluationsList rows={data.evaluations} />
                    )}
                    {tab === 'documents' && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                          <Button type="button" variant="secondary" size="sm" leadingIcon="+" onClick={() => setAddAgreementOpen(true)}>
                            {t('supplier.detail.agreement.add', { defaultValue: 'Add agreement / certificate' })}
                          </Button>
                        </div>
                        <DocumentsList rows={data.documents} />
                      </>
                    )}
                  </div>
                </CardBody>
              </Card>
            </main>

            <aside
              className={styles.sideCol}
              aria-label={t('supplier.detail.actions.aside', {
                defaultValue: 'Supplier actions',
              })}
            >
              <Card>
                <CardHeader>
                  <h2 className={wstyles.sectionTitle}>
                    {t('supplier.detail.actions.title', {
                      defaultValue: 'Actions',
                    })}
                  </h2>
                </CardHeader>
                <CardBody>
                  <div className={styles.actionsList}>
                    <Button
                      variant="primary"
                      onClick={() => setDialog({ kind: 'transition' })}
                      aria-label={t('supplier.detail.actions.transitionAria', {
                        defaultValue: 'Transition supplier lifecycle status',
                      })}
                    >
                      {t('supplier.detail.actions.transition', {
                        defaultValue: 'Transition lifecycle',
                      })}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        navigate({
                          to: '/suppliers/$id/portal',
                          params: { id: supplierId },
                        });
                      }}
                      aria-label={t('supplier.detail.actions.portalAria', {
                        defaultValue: 'Open the supplier self-service portal view',
                      })}
                    >
                      {t('supplier.detail.actions.portal', {
                        defaultValue: 'Supplier portal ↗',
                      })}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setScheduleAuditOpen(true)}
                    >
                      {t('supplier.detail.actions.scheduleAudit', {
                        defaultValue: 'Schedule audit',
                      })}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() =>
                        window.alert(
                          t('supplier.detail.actions.initiateEvaluationStub', {
                            defaultValue:
                              'Initiate evaluation: follow-on task — opens a new vendor scorecard.',
                          }),
                        )
                      }
                    >
                      {t('supplier.detail.actions.initiateEvaluation', {
                        defaultValue: 'Initiate evaluation',
                      })}
                    </Button>
                    <Button
                      variant="tertiary"
                      onClick={() => setDialog({ kind: 'disqualify' })}
                      aria-label={t(
                        'supplier.detail.actions.disqualifyAria',
                        {
                          defaultValue:
                            'Disqualify supplier — dual signature and 200-char rationale required',
                        },
                      )}
                    >
                      {t('supplier.detail.actions.disqualify', {
                        defaultValue: 'Disqualify',
                      })}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </aside>
          </div>

          {dialog.kind === 'transition' && (
            <TransitionDialog
              supplierId={data.supplier_id}
              currentStatus={data.lifecycle_status}
              onClose={() => setDialog({ kind: 'closed' })}
              onTransitioned={() => {
                setDialog({ kind: 'closed' });
                detailQ.refetch();
              }}
            />
          )}

          {dialog.kind === 'disqualify' && (
            <DisqualifyDialog
              supplierId={data.supplier_id}
              supplierName={data.name}
              onClose={() => setDialog({ kind: 'closed' })}
              onDisqualified={() => {
                setDialog({ kind: 'closed' });
                detailQ.refetch();
              }}
            />
          )}

          <AddAgreementDialog
            supplierId={data.supplier_id}
            isOpen={addAgreementOpen}
            onClose={() => setAddAgreementOpen(false)}
          />
          <ScheduleAuditDialog
            supplierId={data.supplier_id}
            isOpen={scheduleAuditOpen}
            onClose={() => setScheduleAuditOpen(false)}
          />
          <RecordAuditOutcomeDialog
            supplierId={data.supplier_id}
            audits={data.audits}
            isOpen={recordOutcomeOpen}
            onClose={() => setRecordOutcomeOpen(false)}
          />
          <RiskAssessmentDialog
            supplierId={data.supplier_id}
            isOpen={riskOpen}
            onClose={() => setRiskOpen(false)}
          />
          <RequalificationDialog
            supplierId={data.supplier_id}
            isOpen={requalOpen}
            onClose={() => setRequalOpen(false)}
          />
          <QuestionnaireDialog
            supplierId={data.supplier_id}
            isOpen={qnOpen}
            onClose={() => setQnOpen(false)}
          />
        </>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * Header — name, criticality + subtype + lifecycle chips, and risk gauge.
 * --------------------------------------------------------------------------- */

interface SupplierHeaderProps {
  name: string;
  criticality: SupplierCriticality;
  subtype: string;
  lifecycleStatus: SupplierLifecycleStatus;
  riskScore: number | null;
  riskCategory: RiskCategory | null;
  nextRequalAtUtc: string | null;
  dateFmt: Intl.DateTimeFormat;
}

function SupplierHeader({
  name,
  criticality,
  subtype,
  lifecycleStatus,
  riskScore,
  riskCategory,
  nextRequalAtUtc,
  dateFmt,
}: SupplierHeaderProps) {
  const { t } = useTranslation();

  const critClass: Record<SupplierCriticality, string> = {
    CRITICAL: styles.critCritical,
    MAJOR: styles.critMajor,
    MINOR: styles.critMinor,
    LOW: styles.critLow,
  };
  const lifeClass: Record<SupplierLifecycleStatus, string> = {
    PENDING_REQUEST: styles.lifePending,
    QUESTIONNAIRE_ISSUED: styles.lifePending,
    AUDIT_SCHEDULED: styles.lifePending,
    ACTIVE: styles.lifeActive,
    RESTRICTED: styles.lifeRestricted,
    DISQUALIFIED: styles.lifeDisqualified,
    ARCHIVED: styles.lifePending,
  };

  return (
    <header className={styles.detailHeader}>
      <div className={styles.detailHeaderMain}>
        <h1 className={styles.detailName}>{name}</h1>
        <div className={styles.detailChips}>
          <span className={`${styles.chip} ${critClass[criticality]}`}>
            {t(`supplier.criticality.${criticality}`, {
              defaultValue: criticality,
            })}
          </span>
          <span className={styles.subtypeText}>
            {t(`supplier.subtype.${subtype}`, { defaultValue: subtype })}
          </span>
          <span className={`${styles.chip} ${lifeClass[lifecycleStatus]}`}>
            {t(`supplier.lifecycle.${lifecycleStatus}`, {
              defaultValue: lifecycleStatus,
            })}
          </span>
        </div>
        {nextRequalAtUtc && (
          <p className={styles.detailNextRequal}>
            {t('supplier.detail.header.nextRequal', {
              defaultValue: 'Next requalification: {{date}}',
              date: dateFmt.format(new Date(nextRequalAtUtc)),
            })}
          </p>
        )}
      </div>
      <RiskGauge score={riskScore} category={riskCategory} />
    </header>
  );
}

/* ----------------------------------------------------------------------------
 * RiskGauge — inline SVG gauge (gradient stroke per QWise palette).
 * --------------------------------------------------------------------------- */

function RiskGauge({
  score,
  category,
}: {
  score: number | null;
  category: RiskCategory | null;
}) {
  const { t, i18n } = useTranslation();
  const nf = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });

  // Risk scale: 0-100 (mock); >=75 critical, etc. We only render the value.
  const v = score == null ? 0 : Math.max(0, Math.min(100, score));
  const r = 50;
  const c = 2 * Math.PI * r;
  const dash = (v / 100) * c;
  const catLabel = category
    ? t(`supplier.risk.${category}`, { defaultValue: category })
    : '—';

  return (
    <section
      className={styles.riskGauge}
      aria-label={t('supplier.detail.header.riskAria', {
        defaultValue: 'Risk score {{v}}, category {{c}}',
        v: score == null ? '—' : nf.format(v),
        c: catLabel,
      })}
    >
      <svg
        width={120}
        height={120}
        viewBox="0 0 120 120"
        role="img"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="sup-risk-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1DD1A1" />
            <stop offset="50%" stopColor="#F59E0B" />
            <stop offset="100%" stopColor="#EF4444" />
          </linearGradient>
        </defs>
        <circle
          cx={60}
          cy={60}
          r={r}
          stroke="var(--color-border)"
          strokeWidth={10}
          fill="none"
        />
        <circle
          cx={60}
          cy={60}
          r={r}
          stroke="url(#sup-risk-grad)"
          strokeWidth={10}
          fill="none"
          strokeDasharray={`${dash} ${c}`}
          strokeLinecap="round"
          transform="rotate(-90 60 60)"
        />
        <text
          x={60}
          y={64}
          textAnchor="middle"
          fontSize={22}
          fontWeight={700}
          fill="var(--color-text)"
        >
          {score == null ? '—' : nf.format(v)}
        </text>
      </svg>
      <div className={styles.riskGaugeLabel}>
        {t('supplier.detail.header.riskLabel', {
          defaultValue: 'Risk · {{c}}',
          c: catLabel,
        })}
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------------
 * EvaluationsList — compact list view; full scorecard lives on its own route.
 * --------------------------------------------------------------------------- */

function EvaluationsList({
  rows,
}: {
  rows: import('./useSupplierDetailQuery').VendorEvaluationRow[];
}) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  if (rows.length === 0) {
    return (
      <p className={styles.tabEmpty}>
        {t('supplier.detail.evaluations.empty', {
          defaultValue: 'No vendor evaluations yet.',
        })}
      </p>
    );
  }

  return (
    <ul className={styles.evalList}>
      {rows.map((r) => (
        <li key={r.evaluation_id} className={styles.evalItem}>
          <div>
            <strong>
              {dateFmt.format(new Date(r.period_start_utc))} —{' '}
              {dateFmt.format(new Date(r.period_end_utc))}
            </strong>
            <div className={styles.subdued}>{r.evaluation_id}</div>
          </div>
          <div className={styles.evalScore}>
            {r.weighted_score == null ? '—' : r.weighted_score}
          </div>
          <div className={styles.evalClass}>
            {r.classification
              ? t(`supplier.evaluation.classification.${r.classification}`, {
                  defaultValue: r.classification,
                })
              : t(`supplier.evaluation.status.${r.status}`, {
                  defaultValue: r.status,
                })}
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ----------------------------------------------------------------------------
 * DocumentsList — supplier-typed document list.
 * --------------------------------------------------------------------------- */

function DocumentsList({
  rows,
}: {
  rows: import('./useSupplierDetailQuery').SupplierDocRow[];
}) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  if (rows.length === 0) {
    return (
      <p className={styles.tabEmpty}>
        {t('supplier.detail.documents.empty', {
          defaultValue: 'No supplier documents linked yet.',
        })}
      </p>
    );
  }

  return (
    <table className={styles.docTable}>
      <caption className={styles.srOnly}>
        {t('supplier.detail.documents.caption', {
          defaultValue: 'Supplier-typed documents linked to this supplier.',
        })}
      </caption>
      <thead>
        <tr>
          <th scope="col">
            {t('supplier.detail.documents.kind', { defaultValue: 'Kind' })}
          </th>
          <th scope="col">
            {t('supplier.detail.documents.title', { defaultValue: 'Title' })}
          </th>
          <th scope="col">
            {t('supplier.detail.documents.effective', {
              defaultValue: 'Effective',
            })}
          </th>
          <th scope="col">
            {t('supplier.detail.documents.expires', {
              defaultValue: 'Expires',
            })}
          </th>
          <th scope="col">
            {t('supplier.detail.documents.state', { defaultValue: 'State' })}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((d) => (
          <tr key={d.document_id}>
            <td>
              <code className={styles.codeChip}>
                {t(`supplier.detail.documents.kindLabel.${d.kind}`, {
                  defaultValue: d.kind,
                })}
              </code>
            </td>
            <td>{d.title}</td>
            <td>
              {d.effective_at_utc
                ? dateFmt.format(new Date(d.effective_at_utc))
                : '—'}
            </td>
            <td>
              {d.expires_at_utc
                ? dateFmt.format(new Date(d.expires_at_utc))
                : '—'}
            </td>
            <td>{d.state}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ----------------------------------------------------------------------------
 * QualificationDossier — consolidated, audit-defensible Vendor Qualification
 * view (VQ6). Surfaces the real VQ data: qualification assessment + documented
 * risk categorization (VQ3), periodic requalification + trend review (VQ2),
 * risk-based audit plan + traceability (VQ4), and conditional-approval
 * justifications (VQ5). Quality agreements + expiry live on the Documents tab.
 * --------------------------------------------------------------------------- */

const VQ_GOOD = '#2f855a';
const VQ_WARN = '#b7791f';
const VQ_BAD = '#c0392b';

function vqColor(status: string): string {
  const s = (status || '').toUpperCase();
  if (['OVERDUE', 'REVOKED', 'EXPIRED', 'NOT_REQUALIFIED'].includes(s)) return VQ_BAD;
  if (['DUE', 'SCHEDULED', 'PLANNED', 'OPEN', 'PENDING', 'IN_PROGRESS'].includes(s)) return VQ_WARN;
  return VQ_GOOD;
}

function VqBadge({ label, color }: { label: string; color: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 999,
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: '0.02em',
        color,
        border: `1px solid ${color}`,
        background: `${color}14`,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

function QualificationDossier({
  data,
}: {
  data: import('./useSupplierDetailQuery').SupplierDetail;
}) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );
  const fmt = (d: string | null | undefined) => (d ? dateFmt.format(new Date(d)) : '—');

  const requalifications = data.requalifications ?? [];
  const assessments = data.assessments ?? [];
  const auditPlan = data.audit_plan ?? [];
  const conditional = data.conditional_approvals ?? [];

  const hasAny =
    requalifications.length || assessments.length || auditPlan.length || conditional.length;

  // Overall qualification posture — any open/overdue obligation surfaces here.
  const attention: string[] = [];
  if (requalifications.some((r) => r.status === 'OVERDUE')) attention.push('requalification overdue');
  if (auditPlan.some((a) => a.status === 'OVERDUE')) attention.push('audit overdue');
  if (conditional.some((c) => c.status === 'OPEN')) attention.push('conditional approval open');
  const posture = attention.length ? 'ACTION REQUIRED' : 'QUALIFIED';

  if (!hasAny) {
    return (
      <p className={styles.tabEmpty}>
        {t('supplier.detail.qualification.empty', {
          defaultValue: 'No vendor-qualification records on file yet.',
        })}
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
      {/* Posture strip */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1.25rem',
          alignItems: 'center',
          padding: '0.75rem 1rem',
          borderRadius: 10,
          border: `1px solid ${posture === 'QUALIFIED' ? VQ_GOOD : VQ_BAD}`,
          background: `${posture === 'QUALIFIED' ? VQ_GOOD : VQ_BAD}10`,
        }}
      >
        <VqBadge label={posture} color={posture === 'QUALIFIED' ? VQ_GOOD : VQ_BAD} />
        {data.risk_category && (
          <span className={styles.subdued}>
            Risk category <strong>{data.risk_category}</strong>
            {data.risk_score != null ? ` (${data.risk_score})` : ''}
          </span>
        )}
        <span className={styles.subdued}>
          Next requalification <strong>{fmt(data.next_requalification_at_utc)}</strong>
        </span>
        {attention.length > 0 && (
          <span className={styles.subdued} style={{ color: VQ_BAD }}>
            {attention.join(' · ')}
          </span>
        )}
      </div>

      {/* Qualification assessments + documented categorization */}
      {assessments.length > 0 && (
        <section>
          <h3 className={wstyles.sectionTitle}>Qualification assessment</h3>
          {assessments.map((a) => (
            <div key={a.assessment_id} style={{ marginTop: '0.5rem' }}>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <strong>{a.assessment_type || 'Assessment'}</strong>
                <VqBadge
                  label={a.recommendation || a.status}
                  color={(a.recommendation || '').includes('CONDITION') ? VQ_WARN : VQ_GOOD}
                />
                <span className={styles.subdued}>
                  Overall {a.overall_score ?? '—'} · reviewed {fmt(a.reviewed_at_utc)}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: '0.5rem', maxWidth: 560 }}>
                {a.sections.map((s) => (
                  <div key={s.key} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 42px', alignItems: 'center', gap: 8 }}>
                    <span className={styles.subdued} title={s.notes || ''}>{s.label}</span>
                    <span style={{ background: '#e2e8f0', borderRadius: 999, height: 8, overflow: 'hidden' }}>
                      <span
                        style={{
                          display: 'block',
                          height: '100%',
                          width: `${Math.max(0, Math.min(100, (s.score / (s.max || 100)) * 100))}%`,
                          background: s.score >= 85 ? VQ_GOOD : s.score >= 70 ? VQ_WARN : VQ_BAD,
                        }}
                      />
                    </span>
                    <strong style={{ textAlign: 'right' }}>{s.score}</strong>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Periodic requalification + performance-trend review */}
      {requalifications.length > 0 && (
        <section>
          <h3 className={wstyles.sectionTitle}>Periodic requalification</h3>
          <table className={styles.docTable}>
            <thead>
              <tr>
                <th scope="col">Cycle</th>
                <th scope="col">Trigger</th>
                <th scope="col">Status</th>
                <th scope="col">Outcome</th>
                <th scope="col">Due / performed</th>
                <th scope="col">Performance trend reviewed</th>
              </tr>
            </thead>
            <tbody>
              {requalifications.map((r) => (
                <tr key={r.requalification_id}>
                  <td>{r.cycle_label}</td>
                  <td><code className={styles.codeChip}>{r.trigger}</code></td>
                  <td><VqBadge label={r.status} color={vqColor(r.status)} /></td>
                  <td>{r.outcome === 'PENDING' ? '—' : (r.outcome || '—')}</td>
                  <td>{r.performed_at_utc ? fmt(r.performed_at_utc) : fmt(r.due_at_utc)}</td>
                  <td className={styles.subdued}>{r.performance_summary || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Risk-based audit plan + traceability */}
      {auditPlan.length > 0 && (
        <section>
          <h3 className={wstyles.sectionTitle}>Audit plan &amp; traceability</h3>
          <table className={styles.docTable}>
            <thead>
              <tr>
                <th scope="col">Year</th>
                <th scope="col">Type</th>
                <th scope="col">Status</th>
                <th scope="col">Planned</th>
                <th scope="col">Outcome → findings → CAPA</th>
              </tr>
            </thead>
            <tbody>
              {auditPlan.map((p) => {
                const a = p.audit;
                const findingsCount = p.findings_count ?? 0;
                const capaLinks = p.capa_links ?? [];
                const trace = a
                  ? `${a.outcome || '—'}${findingsCount ? ` → ${findingsCount} finding(s)` : ''}${capaLinks.length ? ' → CAPA' : ''}`
                  : '—';
                return (
                  <tr key={p.plan_entry_id}>
                    <td>{p.plan_year}</td>
                    <td><code className={styles.codeChip}>{p.audit_type}</code></td>
                    <td><VqBadge label={p.status} color={vqColor(p.status)} /></td>
                    <td>{fmt(p.planned_at_utc)}</td>
                    <td className={styles.subdued}>{trace}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {/* Conditional-approval justifications */}
      {conditional.length > 0 && (
        <section>
          <h3 className={wstyles.sectionTitle}>Conditional approval &amp; justification</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {conditional.map((c) => (
              <div
                key={c.approval_id}
                style={{
                  border: `1px solid ${c.status === 'OPEN' ? VQ_WARN : VQ_GOOD}`,
                  borderRadius: 10,
                  padding: '0.75rem 1rem',
                  background: `${c.status === 'OPEN' ? VQ_WARN : VQ_GOOD}0d`,
                }}
              >
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <VqBadge label={`${c.approval_type} · ${c.status}`} color={vqColor(c.status)} />
                  {c.condition_overdue && <VqBadge label="REVIEW OVERDUE" color={VQ_BAD} />}
                  <span className={styles.subdued}>
                    {c.approver_authority || '—'} · decided {fmt(c.decided_at_utc)}
                    {c.review_by_utc ? ` · review by ${fmt(c.review_by_utc)}` : ''}
                  </span>
                </div>
                <dl style={{ margin: '0.5rem 0 0', display: 'grid', gridTemplateColumns: '170px 1fr', gap: '2px 12px' }}>
                  <dt className={styles.subdued}>Incomplete records</dt>
                  <dd style={{ margin: 0 }}>{c.incomplete_records}</dd>
                  <dt className={styles.subdued}>Justification</dt>
                  <dd style={{ margin: 0 }}>{c.justification}</dd>
                  <dt className={styles.subdued}>Conditions</dt>
                  <dd style={{ margin: 0 }}>{c.conditions}</dd>
                  {c.closure_note && (
                    <>
                      <dt className={styles.subdued}>Closure</dt>
                      <dd style={{ margin: 0 }}>{c.closure_note}</dd>
                    </>
                  )}
                </dl>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
